import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check, Download, Link2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, API, errText } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { Field } from "../components/Field";
import { ScrollRail } from "../components/ScrollRail";
import { ShortlistFacilities } from "../components/ShortlistFacilities";
import { NotAnOffer } from "../components/LegalNotices";
import { StartAgain } from "../components/StartAgain";

const usd = (n) => (Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : `$${Math.round(n || 0).toLocaleString()}`);
const int = (n) => Math.round(n || 0).toLocaleString();
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Stat = ({ label, value, note, testId }) => (
  <div className="bg-[#0b0b0b] p-5" data-testid={testId}>
    <p className="overline !text-zinc-500">{label}</p>
    <p className="mono mt-2 text-xl font-bold text-ochre md:text-2xl">{value}</p>
    {note ? <p className="mt-2 text-[0.62rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p> : null}
  </div>
);

export default function Shortlist() {
  const { token } = useParams();
  const [all, setAll] = useState([]);
  const [picked, setPicked] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [enquiry, setEnquiry] = useState({ name: "", email: "", organisation: "", note: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useSeo({
    title: "County Funding Scenarios",
    description:
      "Pick Kenyan counties and see an indicative combined scenario — build cost, running cost, jobs, players, professionals produced and health value. General football-development research, not an offer, invitation or investment recommendation.",
  });

  useEffect(() => {
    let live = true;
    const shared = token
      ? api.get(`/shortlist/share/${token}`).then((r) => r.data.counties).catch(() => {
          setErr("That shared shortlist link is no longer available. Build your own below.");
          return null;
        })
      : Promise.resolve(null);
    Promise.all([api.get("/priority-counties"), shared])
      .then(([r, sharedCounties]) => {
        if (!live) return;
        setAll(r.data.rows);
        setPicked(sharedCounties?.length ? sharedCounties : r.data.names.slice(0, 5));
        if (sharedCounties?.length) setShareUrl(`${window.location.origin}/county-scenarios/${token}`);
      })
      .catch(() => setErr("We could not load the counties. Refresh to try again."));
    return () => {
      live = false;
    };
  }, [token]);

  useEffect(() => {
    setShareUrl((u) => (token && picked.length ? u : ""));
  }, [picked, token]);

  const share = async () => {
    setSharing(true);
    try {
      const { data: res } = await api.post("/shortlist/share", { counties: picked });
      const url = `${window.location.origin}/county-scenarios/${res.token}`;
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied. Send it to anyone — they will see this exact shortlist.");
      } catch {
        toast.success("Share link ready below.");
      }
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSharing(false);
    }
  };

  const sendEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data: res } = await api.post("/shortlist/enquiry", { ...enquiry, counties: picked });
      setShareUrl(`${window.location.origin}/county-scenarios/${res.token}`);
      setSent(true);
      toast.success(
        res.email_sent
          ? "Enquiry sent. Check your inbox for the pack and your shortlist link."
          : "Enquiry received. We will be in touch.",
      );
      setEnquiry({ name: "", email: "", organisation: "", note: "" });
    } catch (err2) {
      toast.error(errText(err2));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!picked.length) {
      setData(null);
      return;
    }
    api.get(`/shortlist?counties=${picked.map(encodeURIComponent).join(",")}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("We could not price that selection. Try fewer counties."));
  }, [picked]);

  const toggle = (name) =>
    setPicked((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));

  const t = data?.totals;
  const packUrl = useMemo(
    () => `${API}/shortlist-pack.pdf?counties=${picked.map(encodeURIComponent).join(",")}`,
    [picked],
  );

  return (
    <div className="bg-black pb-24 pt-10 md:pt-14" data-testid="shortlist-page">
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">County funding scenarios</p>
            <h1 className="display-1 mt-5 max-w-4xl">Model The Counties You Believe In</h1>
            <p className="body-copy mt-6 max-w-3xl">
              Nobody funds a national programme. Choose counties and this builds one indicative scenario from
              them: what it might cost to build and run, how many players and jobs it could create, how many
              professionals it might produce, what value that could carry, what it could take off the health system,
              and what share of the national plan those counties represent. Then take the scenario away as a PDF.
              Nothing here selects, ranks or recommends an investment for you.
            </p>
            <NotAnOffer className="mt-7 max-w-3xl" />
          </Reveal>

          {err ? (
            <p className="mt-10 border border-maasai/40 bg-maasai/10 p-6 text-sm text-white" data-testid="shortlist-error">
              {err}
            </p>
          ) : null}

          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="shortlist-controls">
            <button
              type="button"
              onClick={() => setPicked(all.filter((r) => r.priority).map((r) => r.county))}
              className="btn-ghost"
              data-testid="shortlist-pick-priority"
            >
              Pick the Priority 18
            </button>
            <button
              type="button"
              onClick={() => setPicked(all.map((r) => r.county))}
              className="btn-ghost"
              data-testid="shortlist-pick-all"
            >
              Pick all 47
            </button>
            <button
              type="button"
              onClick={() => setPicked([])}
              className="btn-ghost"
              data-testid="shortlist-clear"
            >
              Clear
            </button>
            <span className="mono text-[0.62rem] uppercase tracking-[0.16em] text-zinc-500" data-testid="shortlist-count">
              {picked.length} selected
            </span>
            <StartAgain label="Start again" to="/county-scenarios" testId="shortlist-start-again" />
          </div>

          <ScrollRail testId="shortlist-picker" className="mt-6" innerClassName="flex flex-wrap gap-2" progressBar={false}>
            {all.map((r) => {
              const on = picked.includes(r.county);
              return (
                <button
                  key={r.county}
                  type="button"
                  onClick={() => toggle(r.county)}
                  data-testid={`shortlist-toggle-${slug(r.county)}`}
                  className={`flex items-center gap-2 border px-3 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
                    on ? "border-maasai bg-maasai text-white" : "border-white/15 text-zinc-300 hover:border-ochre hover:text-ochre"
                  }`}
                >
                  {on ? <Check size={12} /> : null}
                  {r.county}
                  <span className={`mono ${on ? "text-white/70" : "text-zinc-400"}`}>#{r.rank}</span>
                </button>
              );
            })}
          </ScrollRail>

          {t ? (
            <>
              <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="shortlist-totals">
                <Stat label="Counties selected" value={int(t.counties)} note={`${t.priority_counties} in the Priority 18`} testId="shortlist-counties" />
                <Stat label="Build cost" value={usd(t.capex_usd)} note={`${data.grassroots_share_pct}% grassroots, rest professional`} testId="shortlist-capex" />
                <Stat label="Running cost a year" value={usd(t.opex_usd)} note={`${t.cost_recovery_pct}% covered by trading income`} testId="shortlist-opex" />
                <Stat label="Share of the national plan" value={`${t.capex_share_of_programme_pct}%`} note={`${t.players_share_of_programme_pct}% of its players`} testId="shortlist-share" />
                <Stat label="Players reached a year" value={int(t.players)} note={`${int(t.teams)} teams`} testId="shortlist-players" />
                <Stat label="Permanent jobs" value={int(t.operational_jobs)} note={`${int(t.construction_job_years)} build person-years`} testId="shortlist-jobs" />
                <Stat label="Professionals a year" value={t.pros_per_year} note={`${usd(t.programme_income_usd)} of trading income`} testId="shortlist-pros" />
                <Stat label="Annual social and economic value" value={usd(t.annual_value_usd)} note={`$${t.value_per_dollar} per dollar built`} testId="shortlist-value" />
                <Stat label="Health and productivity value" value={usd(t.health_total_usd)} note={`${int(t.admissions_avoided)} admissions avoided`} testId="shortlist-health" />
                <Stat label="Sick days recovered" value={int(t.sick_days_avoided)} note="Across the working-age players" testId="shortlist-sickdays" />
                <Stat label="Tax back a year" value={usd(t.tax_usd)} note="VAT and PAYE" testId="shortlist-tax" />
                <Stat label="Total annual value" value={usd(t.total_annual_return_usd)} note={`$${t.return_per_dollar} of value per dollar built · surplus matches the build cost in ${t.payback_years} years`} testId="shortlist-return" />
              </div>
              <p className="mt-4 max-w-4xl text-xs leading-relaxed text-zinc-400" data-testid="shortlist-note">{data.note}</p>

              <ShortlistFacilities counties={picked} />

              <div className="mt-8 flex flex-wrap gap-3">
                <a href={packUrl} className="btn-primary" data-testid="shortlist-download">
                  <Download size={14} /> Download this pack
                </a>
                <button type="button" onClick={share} disabled={sharing} className="btn-ghost disabled:opacity-60" data-testid="shortlist-share-button">
                  {sharing ? <Loader2 className="animate-spin" size={14} /> : <Link2 size={14} />} Copy share link
                </button>
                <Link to="/compare" className="btn-ghost" data-testid="shortlist-compare-link">
                  Compare two counties <ArrowUpRight size={14} />
                </Link>
              </div>

              {shareUrl ? (
                <div className="mt-4 flex flex-wrap items-center gap-3 border border-ochre/40 bg-[#0b0b0b] p-4" data-testid="shortlist-share-url">
                  <p className="overline !text-zinc-500">Shareable link</p>
                  <a href={shareUrl} className="mono break-all text-xs text-ochre underline underline-offset-4">
                    {shareUrl}
                  </a>
                </div>
              ) : null}

              <h2 className="display-3 mt-16">What the money buys</h2>
              <div className="mt-6 overflow-x-auto border border-zinc-800" data-testid="shortlist-lines">
                <table className="w-full min-w-[720px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                      {["Project", "Class", "Units", "Build cost"].map((h) => (
                        <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((l, i) => (
                      <tr key={l.item} className="border-b border-zinc-900" data-testid={`shortlist-line-${i}`}>
                        <td className="px-4 py-3 text-sm text-white">{l.item}</td>
                        <td className="px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-maasai">{l.class}</td>
                        <td className="mono px-4 py-3 text-sm text-zinc-300">{l.units}</td>
                        <td className="mono px-4 py-3 text-sm text-ochre">{usd(l.capex_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="display-3 mt-16">County by county</h2>
              <div className="mt-6 overflow-x-auto border border-zinc-800" data-testid="shortlist-rows">
                <table className="w-full min-w-[1020px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                      {["County", "Rank", "Units", "Build cost", "Jobs", "Players", "Pros a year", "Annual value", "Value per $1", "Dossier"].map((h) => (
                        <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={r.county} className="border-b border-zinc-900" data-testid={`shortlist-row-${i}`}>
                        <td className="px-4 py-3 text-sm text-white">{r.county}</td>
                        <td className="mono px-4 py-3 text-sm text-maasai">#{r.priority_rank}</td>
                        <td className="mono px-4 py-3 text-sm text-zinc-300">{r.project_count}</td>
                        <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.capex_usd)}</td>
                        <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.operational_jobs)}</td>
                        <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.players)}</td>
                        <td className="mono px-4 py-3 text-sm text-zinc-300">{r.pros_per_year}</td>
                        <td className="mono px-4 py-3 text-sm text-ochre">{usd(r.annual_value_usd)}</td>
                        <td className="mono px-4 py-3 text-sm text-zinc-300">${r.value_per_dollar}</td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/counties/${slug(r.county)}`}
                            data-testid={`shortlist-dossier-${i}`}
                            className="text-[0.6rem] font-bold uppercase tracking-[0.14em] text-ochre underline underline-offset-4 hover:text-white"
                          >
                            Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-10 border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="shortlist-method">
                <p className="overline text-ochre">The method</p>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{data.method}</p>
              </div>

              <div className="mt-20 grid gap-12 lg:grid-cols-[1fr_1.1fr]" data-testid="shortlist-enquiry">
                <Reveal>
                  <p className="eyebrow">Preliminary expression of interest</p>
                  <h2 className="display-3 mt-5">Send This Scenario To Us</h2>
                  <p className="body-copy mt-6">
                    Send the {t.counties} counties above to our team with a note about what interests you. We
                    email you the scenario pack and your own link, and reply within two working days.
                  </p>
                  <p className="mt-6 max-w-md text-xs leading-relaxed text-zinc-400">
                    Every figure is a labelled estimate, published so it can be challenged.
                  </p>
                </Reveal>

                <Reveal delay={0.1}>
                  <form
                    onSubmit={sendEnquiry}
                    className="grid gap-5 border border-zinc-900 bg-ink-surface p-7 sm:grid-cols-2 md:p-9"
                    data-testid="shortlist-enquiry-form"
                  >
                    <p className="overline sm:col-span-2">Your details</p>
                    <Field label="Full name" required>
                      <input required className="field" placeholder="Jane Achieng" aria-label="Full name" data-testid="enquiry-name" value={enquiry.name} onChange={(e) => setEnquiry({ ...enquiry, name: e.target.value })} />
                    </Field>
                    <Field label="Email" required>
                      <input required type="email" className="field" placeholder="you@example.com" aria-label="Email" data-testid="enquiry-email" value={enquiry.email} onChange={(e) => setEnquiry({ ...enquiry, email: e.target.value })} />
                    </Field>
                    <Field label="Organisation" className="sm:col-span-2">
                      <input className="field" placeholder="Fund, company, county or none" aria-label="Organisation" data-testid="enquiry-org" value={enquiry.organisation} onChange={(e) => setEnquiry({ ...enquiry, organisation: e.target.value })} />
                    </Field>
                    <Field label="Your note" required className="sm:col-span-2" hint="What you would fund, how much you have in mind and what you need from us next.">
                      <textarea required rows={7} minLength={10} className="field" placeholder="We are interested in the grassroots build across these counties" aria-label="Your note" data-testid="enquiry-note" value={enquiry.note} onChange={(e) => setEnquiry({ ...enquiry, note: e.target.value })} />
                    </Field>
                    <button type="submit" disabled={sending} className="btn-primary justify-center disabled:opacity-60 sm:col-span-2" data-testid="enquiry-submit">
                      {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send shortlist and enquiry
                    </button>
                    {sent ? (
                      <p className="text-xs text-ochre sm:col-span-2" data-testid="enquiry-sent">
                        Enquiry sent. The pack and your shortlist link are on their way to your inbox.
                      </p>
                    ) : null}
                  </form>
                </Reveal>
              </div>
            </>
          ) : (
            <p className="mt-12 border border-white/10 bg-[#0b0b0b] p-6 text-sm text-zinc-400" data-testid="shortlist-empty">
              Choose at least one county to price a shortlist.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
